import { SubjectModalProps } from '../types';
import DialogModal from '../../../components/DialogModal';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import subjectAndGroupingApis from '../../../apis/subjects';
import DropdownSelect from '../../../components/DropdownSelect';
import { StyledButton } from '../../Exams/styled';
import {
  reportGroupOptions,
  reportSubGroupOptions,
  subjectTypeOptions
} from '../config/constant';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import SelectAll from '../../../components/SelectAllField/SelectAllField';

const AddSubjectModal: React.FC<SubjectModalProps> = ({
  fetchData,
  open,
  onClose,
  onApiSuccessAndFailure,
  divisionOptions,
  grade
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, setName] = useState<string>('');
  const [division, setDivision] = useState<string[]>([]);
  const [type, setType] = useState<string>('');
  const [isElective, setIsElective] = useState<boolean>(false);
  const [reportCardLabel, setReportCardLabel] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [orderSequence, setOrderSequence] = useState<number | undefined>(
    undefined
  );
  const [reportGroup, setReportGroup] = useState<string>('');
  const [reportSubGroup, setReportSubGroup] = useState<string>('');

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const resetAllStates = () => {
    setIsSubmitting(false);
    setName('');
    setDivision([]);
    setType('');
    setIsElective(false);
    setReportCardLabel('');
    setCode('');
    setOrderSequence(undefined);
    setReportGroup('');
    setReportSubGroup('');
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const payloads = division.map((div) => ({
      name,
      grade,
      division: div,
      type,
      isElective,
      reportCardLabel: reportCardLabel || undefined,
      code: code || undefined,
      orderSequence: orderSequence || undefined,
      reportGroup: reportGroup || undefined,
      reportSubGroup: reportSubGroup || undefined
    }));

    try {
      const response = await subjectAndGroupingApis.addSubject(
        loggedInUser.school,
        payloads
      );
      if (response) {
        fetchData();
        onApiSuccessAndFailure(true, 'Subject updated successfully');
      }
    } catch (error: any) {
      onApiSuccessAndFailure(
        false,
        error.errors[0].message || 'Error adding subject'
      );
    } finally {
      resetAllStates();
    }
  };

  const handleModalClose = () => {
    onClose();
    resetAllStates();
  };

  return (
    <>
      <DialogModal
        open={open}
        onClose={handleModalClose}
        title='Add Subject'
        actions={
          <>
            <Button
              onClick={handleModalClose}
              color='primary'
              variant='outlined'
            >
              Cancel
            </Button>
            <StyledButton
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                !name.trim() ||
                !grade ||
                division.length === 0 ||
                !type
              }
            >
              {isSubmitting ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Add'
              )}
            </StyledButton>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextInputWithIcon
            fullWidth
            id='name'
            name='name'
            placeholder='Enter Subject Name'
            label='Subject Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <TextInputWithIcon
            fullWidth
            id='grade'
            name='grade'
            label='Grade'
            placeholder='Enter Grade'
            value={grade}
            required
            disabled
          />

          <SelectAll
            label='Division'
            required
            placeholder='Select Division'
            options={divisionOptions ?? []}
            selected={division}
            onChange={setDivision}
          />
          <DropdownSelect
            label='Subject Type'
            placeholder='Select Subject Type'
            name='type'
            value={type}
            options={subjectTypeOptions}
            onChange={(e) => setType(e.target.value as string)}
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={isElective}
                onChange={(e) => setIsElective(e.target.checked)}
                name='isElective'
                color='primary'
              />
            }
            label='Is Elective'
          />

          <Typography variant='subtitle2' color='textSecondary'>
            ** All fields below this section are used for report card. So
            specify only if applicable, otherwise report-card design can go
            wrong. These fields are not applicable if subject is to be created
            for DCU.
          </Typography>

          <TextInputWithIcon
            fullWidth
            id='reportCardLabel'
            name='reportCardLabel'
            label='Report Card Label'
            placeholder='Enter Report Card Label'
            value={reportCardLabel}
            onChange={(e) => setReportCardLabel(e.target.value)}
          />
          <TextInputWithIcon
            fullWidth
            id='code'
            name='code'
            label='Subject Code'
            placeholder='Enter Subject Code'
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <TextInputWithIcon
            fullWidth
            id='orderSequence'
            name='orderSequence'
            label='Order Sequence'
            placeholder='Enter Order Sequence'
            type='number'
            value={orderSequence === undefined ? '' : orderSequence}
            onChange={(e) =>
              setOrderSequence(
                e.target.value === '' ? undefined : Number(e.target.value)
              )
            }
            slotProps={{
              htmlInput: {
                min: 0
              }
            }}
          />
          <DropdownSelect
            label='Report Group'
            placeholder='Select Report Group'
            required
            name='reportGroup'
            value={reportGroup}
            options={reportGroupOptions}
            onChange={(e) => setReportGroup(e.target.value as string)}
          />
          <DropdownSelect
            label='Report Sub-Group'
            placeholder='Select Report Sub-Group'
            required
            name='reportSubGroup'
            value={reportSubGroup}
            options={reportSubGroupOptions}
            onChange={(e) => setReportSubGroup(e.target.value as string)}
          />
        </Box>
      </DialogModal>
    </>
  );
};

export default AddSubjectModal;
